"use client";

import { type ReactNode, type Ref, useEffect, useRef, useState } from "react";

export function DeferredDashboardWidget({
  title,
  render,
}: {
  title: string;
  render: () => ReactNode;
}) {
  const root = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setVisible(true);
        observer.disconnect();
      },
      { rootMargin: "600px 0px" },
    );
    if (root.current) observer.observe(root.current);
    return () => observer.disconnect();
  }, [visible]);

  return (
    <DeferredDashboardWidgetView title={title} visible={visible} rootRef={root}>
      {visible ? render() : null}
    </DeferredDashboardWidgetView>
  );
}

/** A widget's slot: the widget once it nears the screen, a pulsing placeholder until then. */
export function DeferredDashboardWidgetView({
  title,
  visible,
  rootRef,
  children,
}: {
  title: string;
  /** The widget is near the screen and shows; a static dashboard passes true. */
  visible: boolean;
  rootRef?: Ref<HTMLDivElement>;
  children?: ReactNode;
}) {
  return (
    <div ref={rootRef} className="h-full min-h-0">
      {visible ? (
        children
      ) : (
        <div
          role="status"
          aria-label={`Loading ${title}`}
          className="h-full min-h-20 animate-pulse rounded-lg border border-border/50 bg-muted/20"
        />
      )}
    </div>
  );
}
